<section class="br-card br-compact-page rolloff-workspace-page">
  <header class="br-card-header workspace-collection-header">
    <h1 class="workspace-page__title">Roll-off Deals</h1>
    <button type="button"
            class="br-btn br-btn--primary br-btn--sm workspace-collection-add"
            *ngIf="canManage" (click)="openCreate()">
      <br-icon>add</br-icon> New deal
    </button>
  </header>
  <div class="br-card-body">

  <!-- The pipeline as a track, with the counts the columns already carry.
       The same control the Leads collection uses - shared rather than copied;
       see components/milestone-rail, which this page is the second caller of.
       Focusing a stage narrows the QUERY, so what a column then holds is the
       stage and not whichever hires happened to be in the loaded page. -->
  <div class="ro-rail-row" *ngIf="!loadError && columns.length">
    <app-milestone-rail class="ro-rail"
                        [stops]="railStops"
                        [selected]="stageFocus"
                        [total]="railTotal"
                        (selectedChange)="focusStage($event)"></app-milestone-rail>
    <!-- The same lasting offer the Leads board makes, shared button and all.
         Whether a way back exists is the server's transition rule - one rule
         for hires and leads - so a refused undo says so on the card. -->
    <app-undo-button *ngIf="canManage"
                     [count]="undoCount" [busy]="undoing" [tooltip]="undoTooltip"
                     (undo)="undoLastMove()"></app-undo-button>
  </div>

  <div class="workspace-filters">
    <mat-form-field appearance="outline" class="ro-search" subscriptSizing="dynamic">
      <mat-label>Search site, address or city</mat-label>
      <input matInput [(ngModel)]="search" (keyup.enter)="load()" />
    </mat-form-field>

    <!-- Column rules, in the Leads list's grammar, behind the same Filters
         button. What is this page's is the column list (rolloff-filters.ts);
         the bar, the panel and the parameter format are shared. -->
    <app-data-filter [fields]="filterFields"
                     [values]="filterValues"
                     (change)="applyFilters($event)"></app-data-filter>

    <mat-chip-listbox class="ro-chips" aria-label="Quick filters" multiple>
      <mat-chip-option *ngIf="canFilterMine" [selected]="minedOnly"
                       (selectionChange)="minedOnly = $event.selected; load()">
        Assigned to me
      </mat-chip-option>
    </mat-chip-listbox>

    <!-- "Show details" rather than "Card density": density names the
         mechanism, and what somebody wants is the extra fields. Same control,
         same wording and same icons as the Leads board. -->
    <button mat-icon-button class="ro-filter-icon"
            [matTooltip]="density === 'compact' ? 'Show details' : 'Hide details'"
            [attr.aria-label]="density === 'compact' ? 'Show details' : 'Hide details'"
            [attr.aria-pressed]="density === 'detailed'"
            (click)="density = density === 'compact' ? 'detailed' : 'compact'">
      <br-icon>{{ density === 'compact' ? 'density_small' : 'density_medium' }}</br-icon>
    </button>

    <!-- No Refresh: the search, the filters, the chip and the rail each read
         the board again, and a move or an edit redraws the card it changed.
         Reported as a button that did nothing useful, so it is gone.

         Rows in, rows out, rows folded and rows gone are here, though. They
         are about the DEALS this board draws, and sending somebody to the
         Table view to reach them was a view switch for nothing. The three on
         the left are the same shared component the Table view carries; Delete
         All is the account-level action, tucked behind the same menu the
         Leads band puts it behind. -->
    <span class="br-table-actions">
      <app-table-transfer-actions table="rolloff-hires" title="roll-off deals"
                                  [exportUrl]="exportUrl"
                                  exportFileName="rolloff-deals.csv"
                                  (changed)="load()"></app-table-transfer-actions>

      <button type="button"
              class="br-btn br-btn--outline-secondary br-btn--sm br-icon-btn"
              *ngIf="canDeleteAllData"
              [brMenuTriggerFor]="dealDataActions"
              aria-label="Roll-off deal data actions"
              [disabled]="deletingAll">
        <br-icon>more_horiz</br-icon>
      </button>
      <ng-template #dealDataActions>
        <br-menu aria-label="Deal data actions">
          <button brMenuItem (triggered)="deleteAllDeals()" data-test="delete-all-deals" label="Delete All" icon="delete_sweep"></button>
        </br-menu>
      </ng-template>
    </span>

    <span class="ro-data-note" *ngIf="deleteAllNotice">{{ deleteAllNotice }}</span>
    <span class="ro-data-error" *ngIf="deleteAllError">
      <br-icon>error_outline</br-icon> {{ deleteAllError }}
    </span>
  </div>

  <!-- Held decisions about hires, on the hires page. Scoped by object type, so
       a lead's approval never appears here and this board draws nothing when
       the agent is holding nothing of its own. -->
  <app-approvals-panel objectType="RollOffOpportunity"
                       (decided)="load()"></app-approvals-panel>

  <div class="ro-error" *ngIf="loadError">
    <br-icon>error_outline</br-icon> {{ loadError }}
  </div>

  <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>

  <!-- More matched than are drawn. Kept alongside the per-column counters
       because it says something they cannot: which hires are missing depends
       on the id order the page was cut on, so the remedy is the search, not
       scrolling a column. -->
  <div class="ro-truncated" *ngIf="truncated">
    Showing {{ items.length }} of {{ total }} deals — each column says how many
    it holds; narrow the search to draw the rest.
  </div>

  <!-- The board. Columns come from the catalog; a drop opens the confirmation
       and never moves the card. -->
  <!-- `cdkScrollable` registers this strip with the CDK: 1500px of columns
       render in a 677px pane, and without it a drag neither auto-scrolls to
       the columns past the right edge nor re-reads the column rectangles it
       measured when the drag started, which any scroll invalidates. The pane
       this board scrolls vertically inside is registered by
       workspace-master-detail. -->
  <div class="ro-board" *ngIf="!loadError" cdkDropListGroup cdkScrollable>
    <!-- While a card is in the air the columns say which of them will take it:
         the offer is the point of sending the transitions, so it has to be
         visible during the drag and not only explained after one. -->
    <!-- The shared column, not a second set of CDK wiring. It owns the two
         things both boards have already been wrong about - every column's list
         being the same rectangle, and the list being registered with
         cdkScrollable - and neither is visible in a screenshot. What stays
         here is everything a person sees: the head, the cards and the colours,
         which is the caller's on purpose. -->
    <app-board-column class="ro-column" *ngFor="let column of columns; trackBy: trackByColumn"
         [class.ro-column-finished]="isFinished(column)"
         [class.ro-column-legal]="isLegalTarget(column.code)"
         [class.ro-column-blocked]="isBlockedTarget(column.code)"
         [data]="column.code"
         (dropped)="onDrop($event, column.code)">

      <!-- The header states what the column HOLDS, from the server, and says
           separately how much of it is drawn. Counting the cards on screen
           would agree with the screen and misrepresent the database — a
           column reading "0" while it holds thirty-seven. -->
      <div class="ro-column-head" [style.border-top-color]="stageColor(column.code)">
        <span class="ro-column-title" [style.color]="stageColor(column.code)">
          {{ column.name }}
        </span>
        <span class="ro-column-count">{{ columnTotal(column.code) }}</span>
      </div>
      <div class="ro-column-stats">
        <span class="ro-column-quoted" *ngIf="columnQuoted(column.code) as quoted">
          {{ quoted | currency:'USD':'symbol':'1.0-0' }}
        </span>
        <!-- Only when they differ. On the ordinary board every card is loaded
             and a permanent "12 of 12" is noise. -->
        <span class="ro-column-shown" *ngIf="columnIsTruncated(column.code)">
          showing {{ columnCount(column.code) }} of {{ columnTotal(column.code) }}
        </span>
      </div>

      <div class="ro-card" *ngFor="let hire of itemsFor(column.code); trackBy: trackByHire"
           cdkDrag [cdkDragData]="hire" [cdkDragDisabled]="!canManage"
           (cdkDragStarted)="onDragStarted(hire)" (cdkDragEnded)="onDragEnded()"
           tabindex="0" (keyup.enter)="onCardEnter($event, hire)">

        <div class="ro-card-title">
          <button type="button" class="ro-open-btn" (click)="openDetail(hire)">
            {{ hire.siteName }}
          </button>
        </div>

        <div class="ro-card-meta">
          <!-- The catalog's wording, or the phrase a rep typed before there
               was a catalog. The second is marked, so nobody reads "roro" as
               a configured size. -->
          <span class="ro-size" *ngIf="containerLabel(hire) as size"
                [class.ro-size-unplaced]="isUnplacedSize(hire)"
                [matTooltip]="isUnplacedSize(hire)
                  ? 'Recorded before there was a size list — edit the deal to pick one'
                  : ''">{{ size }}</span>
          <span class="ro-money" *ngIf="hire.quotedPrice != null">
            {{ hire.quotedPrice | currency:'USD':'symbol':'1.0-0' }}
          </span>
        </div>

        <!-- What "Show details" adds. Additive on purpose: the compact card
             is exactly the card this board has always drawn, so turning the
             control on reveals a field and turning it off takes nothing away
             that was there before it existed. -->
        <div class="ro-card-meta ro-card-where" *ngIf="density === 'detailed'">
          <br-icon class="ro-mini-icon">place</br-icon>
          <span class="ro-where">{{ address(hire) }}</span>
        </div>

        <div class="ro-card-meta" *ngIf="hireDates(hire) as dates">
          <span class="ro-dates"><br-icon class="ro-mini-icon">event</br-icon> {{ dates }}</span>
        </div>

        <div class="ro-card-meta">
          <span class="ro-owner">{{ ownerLabel(hire) }}</span>
        </div>

        <div class="ro-card-actions" *ngIf="canManage">
          <button type="button" class="br-btn br-btn--link br-btn--sm"
                  *ngIf="availableTargets(hire).length" (click)="startMove(hire)">
            <br-icon>swap_horiz</br-icon> Move
          </button>
          <button type="button" class="br-btn br-btn--link br-btn--sm" (click)="openEdit(hire)">
            <br-icon>edit</br-icon> Edit
          </button>
        </div>

        <!-- The confirmation. The card is still in its own column behind this
             panel, and stays there unless the server says otherwise. -->
        <div class="ro-move-panel" *ngIf="moveTarget?.id === hire.id">
          <div class="ro-move-heading" *ngIf="moveTo">
            Move to <strong>{{ columnLabel(moveTo) }}</strong>?
          </div>
          <mat-form-field appearance="outline" class="ro-note" subscriptSizing="dynamic">
            <!-- The server stores this and never demands it. -->
            <mat-label>Why? (optional, kept on the history)</mat-label>
            <input matInput [(ngModel)]="moveNote" />
          </mat-form-field>

          <div class="ro-move-targets" *ngIf="moveTo; else allTargets">
            <button class="br-btn br-btn--primary br-btn--sm" [disabled]="moving"
                    (click)="confirmMove(hire, moveTo)">
              {{ moving ? 'Moving…' : 'Confirm' }}
            </button>
            <button class="br-btn br-btn--outline-secondary br-btn--sm" [disabled]="moving"
                    (click)="cancelMove()">Cancel</button>
          </div>
          <ng-template #allTargets>
            <!-- Only the legal targets, so there is no button here that the
                 server would refuse. -->
            <div class="ro-move-targets">
              <button class="br-btn br-btn--outline-secondary br-btn--sm"
                      *ngFor="let target of availableTargets(hire)"
                      [disabled]="moving" (click)="confirmMove(hire, target)">
                {{ columnLabel(target) }}
              </button>
              <button class="br-btn br-btn--link br-btn--sm" [disabled]="moving"
                      (click)="cancelMove()">Cancel</button>
            </div>
          </ng-template>

          <div class="ro-move-error" *ngIf="moveError">
            <br-icon>block</br-icon> {{ moveError }}
          </div>
        </div>
      </div>

      <p class="ro-empty" *ngIf="!columnCount(column.code)">—</p>
    </app-board-column>
  </div>

  <p class="ro-empty-board" *ngIf="!loadError && !loading && !items.length && columns.length">
    No roll-off deals match this filter.
  </p>

  </div>

</section>
